import { useRef, useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Stage, Layer, Image as KonvaImage, Transformer, Circle, Path } from 'react-konva';
import { Loader2, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Plus, Minus, RotateCw } from 'lucide-react';
import useImage from 'use-image';
import { documentsAPI, pendingDocumentsAPI } from '../services/api';
import './DocumentViewer.css';

// Настройка worker для PDF.js - используем unpkg CDN
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// Компонент для печати в Konva
function StampImage({ stamp, isSelected, onSelect, onDragEnd, onTransformEnd, imageUrl }) {
  const [image] = useImage(imageUrl || '');
  const shapeRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current && image) {
      // Небольшая задержка для гарантии что все отрендерилось
      setTimeout(() => {
        if (trRef.current && shapeRef.current) {
          trRef.current.nodes([shapeRef.current]);
          trRef.current.getLayer().batchDraw();
        }
      }, 10);
    }
  }, [isSelected, image, stamp.id]);

  if (!image) return null;

  return (
    <>
      <KonvaImage
        image={image}
        x={stamp.x}
        y={stamp.y}
        width={stamp.width}
        height={stamp.height}
        rotation={stamp.rotation || 0}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={onDragEnd}
        onTransformEnd={onTransformEnd}
        ref={shapeRef}
        shadowEnabled={true}
        shadowBlur={isSelected ? 15 : 5}
        shadowColor={isSelected ? "rgba(0, 122, 255, 0.6)" : "rgba(0, 0, 0, 0.2)"}
        shadowOffsetX={0}
        shadowOffsetY={isSelected ? 4 : 2}
        opacity={isSelected ? 0.95 : 1}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          keepRatio={true}
          boundBoxFunc={(oldBox, newBox) => {
            // Ограничиваем минимальный и максимальный размер
            const minSize = 10;
            const maxSize = 200;
            
            if (Math.abs(newBox.width) < minSize || Math.abs(newBox.height) < minSize) {
              return oldBox;
            }
            
            if (Math.abs(newBox.width) > maxSize || Math.abs(newBox.height) > maxSize) {
              return oldBox;
            }
            
            return newBox;
          }}
          rotateEnabled={true}
          enabledAnchors={[
            'top-left',
            'top-right',
            'bottom-left',
            'bottom-right',
          ]}
          // Кастомные стили для маркеров resize (углы)
          anchorSize={20}
          anchorStroke="#007aff"
          anchorStrokeWidth={3}
          anchorFill="white"
          anchorCornerRadius={10}
          anchorShadowColor="rgba(0, 122, 255, 0.5)"
          anchorShadowBlur={8}
          anchorShadowOffsetX={0}
          anchorShadowOffsetY={2}
          // Стили для рамки
          borderStroke="#007aff"
          borderStrokeWidth={2}
          borderDash={[8, 4]}
          // Кастомные стили для маркера rotate
          rotateAnchorOffset={40}
          rotateAnchorCursor="grab"
          // Стиль самого rotator anchor
          rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
          rotationSnapTolerance={5}
        />
      )}
    </>
  );
}

function DocumentViewer({ 
  document, 
  pendingDocument,
  selectedStamp, 
  placedStamps, 
  onStampPlace, 
  onStampUpdate,
  onRemoveStamp,
  onClearSelection,
  onDocumentDimensionsChange, // Новый пропс для передачи размеров
  onStampEditingChange // Новый пропс для уведомления о режиме редактирования
}) {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const pdfCanvasRef = useRef(null);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(true);
  const [documentType, setDocumentType] = useState(null);
  const [selectedStampId, setSelectedStampId] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 1000 });
  const [pageImage, setPageImage] = useState(null);
  const [originalDimensions, setOriginalDimensions] = useState({ width: 0, height: 0 }); // Оригинальные размеры документа
  const [lastDist, setLastDist] = useState(0);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const isPending = pendingDocument?.isPending;
  const currentDoc = isPending ? pendingDocument : document;

  // Автоматическое размещение печати при выборе
  useEffect(() => {
    if (selectedStamp && stageSize.width > 0 && stageSize.height > 0) {
      // Загружаем изображение печати (используем medium версию для быстрой загрузки)
      const img = new window.Image();
      const imageUrl = `${import.meta.env.VITE_API_URL}/stamps/${selectedStamp.id}/medium?initData=${encodeURIComponent(window.Telegram?.WebApp?.initData || '')}`;
      
      img.onload = () => {
        // Вычисляем размер с сохранением пропорций
        const aspectRatio = img.width / img.height;
        const targetSize = 70; // Целевой размер для большей стороны
        
        let baseWidth, baseHeight;
        
        // Определяем, какая сторона больше, и делаем её 70px
        if (img.width >= img.height) {
          // Горизонтальная или квадратная подпись
          baseWidth = targetSize;
          baseHeight = targetSize / aspectRatio;
        } else {
          // Вертикальная подпись
          baseHeight = targetSize;
          baseWidth = targetSize * aspectRatio;
        }
        
        // Размещаем в центре документа
        const newStamp = {
          id: Date.now(),
          stampId: selectedStamp.id,
          x: (stageSize.width - baseWidth) / 2,
          y: (stageSize.height - baseHeight) / 2,
          width: baseWidth,
          height: baseHeight,
          rotation: 0,
          pageNumber: currentPage - 1,
          imageUrl: imageUrl
        };

        onStampPlace(newStamp);
        
        // Выбираем печать для редактирования
        setTimeout(() => {
          setSelectedStampId(newStamp.id);
          if (onStampEditingChange) {
            onStampEditingChange(true);
          }
        }, 50);
        
        // Очищаем выбор печати из селектора
        if (onClearSelection) {
          onClearSelection();
        }
      };
      
      img.src = imageUrl;
    }
  }, [selectedStamp, stageSize, currentPage]);

  // Функция для вычисления расстояния между двумя точками касания
  const getDistance = (p1, p2) => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  };

  // Обработка pinch-to-zoom и panning
  useEffect(() => {
    const container = containerRef.current;
    const stage = stageRef.current;
    
    if (!container || !stage) {
      return;
    }

    const handleTouchStart = (e) => {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      // Если один палец и документ зазумлен - проверяем, не на печати ли мы кликнули
      if (touch1 && !touch2 && scale > 1) {
        const target = e.target;
        
        // Проверяем, не кликнули ли мы на Konva canvas
        // Также проверяем, не находимся ли мы в режиме редактирования печати
        const isKonvaCanvas = target.tagName === 'CANVAS' && target.className.includes('konvajs');
        
        // Не активируем panning если:
        // 1. Касание на Konva canvas (где находятся печати)
        // 2. Или уже выбрана какая-то печать
        if (!isKonvaCanvas && !selectedStampId) {
          setIsDragging(true);
          setDragStart({
            x: touch1.clientX - stagePos.x,
            y: touch1.clientY - stagePos.y,
          });
        }
      }
    };

    const handleTouchMove = (e) => {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      // Если два пальца - это pinch zoom
      if (touch1 && touch2) {
        e.preventDefault();
        setIsDragging(false); // Отключаем drag во время pinch
        
        const p1 = {
          x: touch1.clientX,
          y: touch1.clientY,
        };
        const p2 = {
          x: touch2.clientX,
          y: touch2.clientY,
        };

        const dist = getDistance(p1, p2);

        if (lastDist === 0) {
          setLastDist(dist);
          return;
        }

        // Вычисляем центральную точку между пальцами
        const centerX = (p1.x + p2.x) / 2;
        const centerY = (p1.y + p2.y) / 2;

        // Получаем позицию Stage относительно viewport
        const stageContainer = stage.container();
        if (!stageContainer) return;
        
        const stageBox = stageContainer.getBoundingClientRect();
        if (!stageBox || stageBox.width === 0 || stageBox.height === 0) return;
        
        // Преобразуем в координаты Stage
        const pointerPos = {
          x: (centerX - stageBox.left - stagePos.x) / scale,
          y: (centerY - stageBox.top - stagePos.y) / scale,
        };

        const delta = dist - lastDist;
        const scaleBy = 1.02;
        const oldScale = scale;
        const newScale = delta > 0 ? oldScale * scaleBy : oldScale / scaleBy;
        
        // Ограничиваем масштаб
        const clampedScale = Math.max(0.5, Math.min(2, newScale));

        // Вычисляем новую позицию Stage для zoom к точке
        const newPos = {
          x: centerX - stageBox.left - pointerPos.x * clampedScale,
          y: centerY - stageBox.top - pointerPos.y * clampedScale,
        };
        
        setScale(clampedScale);
        setStagePos(newPos);
        setLastDist(dist);
      } 
      // Если один палец и мы в режиме перетаскивания
      else if (touch1 && isDragging && scale > 1) {
        e.preventDefault();
        
        const newPos = {
          x: touch1.clientX - dragStart.x,
          y: touch1.clientY - dragStart.y,
        };

        setStagePos(newPos);
      }
    };

    const handleTouchEnd = () => {
      setLastDist(0);
      setIsDragging(false);
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [scale, lastDist, stagePos, loading, documentUrl, isDragging, dragStart, selectedStampId]);

  useEffect(() => {
    // Сбрасываем состояние при смене документа
    setPageImage(null);
    setDocumentUrl(null);
    setNumPages(null);
    setCurrentPage(1);
    setLoading(true);
    
    loadDocument();
    
    // Очищаем Blob URL при размонтировании компонента
    return () => {
      if (documentUrl && documentUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(documentUrl);
      }
    };
  }, [currentDoc]);

  useEffect(() => {
    updateStageSize();
    window.addEventListener('resize', updateStageSize);
    return () => window.removeEventListener('resize', updateStageSize);
  }, []);

  useEffect(() => {
    // Повторно обновляем размер при загрузке документа
    const timer = setTimeout(() => {
      updateStageSize();
    }, 100);
    return () => clearTimeout(timer);
  }, [documentUrl, currentPage]);

  const updateStageSize = () => {
    if (containerRef.current) {
      const container = containerRef.current;
      setStageSize({
        width: container.clientWidth - 40,
        height: Math.max(600, container.clientHeight - 200)
      });
    }
  };

  const loadDocument = async () => {
    try {
      setLoading(true);
      
      if (!currentDoc) {
        setLoading(false);
        return;
      }

      // Загружаем документ через API с авторизацией
      let response;
      if (isPending) {
        response = await pendingDocumentsAPI.download(currentDoc.id);
      } else {
        response = await documentsAPI.download(currentDoc.id);
      }

      // Все документы теперь приходят как PDF (конвертируются на сервере при загрузке)
      const fileType = 'pdf';
      const mimeType = 'application/pdf';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      
      setDocumentUrl(url);
      setDocumentType(fileType);
      setLoading(false);
    } catch (error) {
      console.error('Ошибка загрузки документа:', error);
      setLoading(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
  };

  const onPageRenderSuccess = (page) => {
    // Когда страница PDF отрендерилась, конвертируем её в изображение
    try {
      if (pdfCanvasRef.current) {
        const canvas = pdfCanvasRef.current;
        // Небольшая задержка для гарантии что canvas готов
        setTimeout(() => {
          if (canvas) {
            const dataURL = canvas.toDataURL('image/png');
            const img = new window.Image();
            img.onload = () => {
              setPageImage(img);
              
              // Сохраняем оригинальные размеры PDF страницы
              const originalWidth = img.width;
              const originalHeight = img.height;
              setOriginalDimensions({ width: originalWidth, height: originalHeight });
              
              // Обновляем размер Stage под реальный размер страницы
              const aspect = originalWidth / originalHeight;
              const maxWidth = (containerRef.current?.clientWidth || 800) - 40;
              const maxHeight = Math.max(600, (containerRef.current?.clientHeight || 1000) - 200);
              
              let newWidth = maxWidth;
              let newHeight = newWidth / aspect;
              
              if (newHeight > maxHeight) {
                newHeight = maxHeight;
                newWidth = newHeight * aspect;
              }
              
              setStageSize({
                width: newWidth,
                height: newHeight
              });
              
              // Сообщаем родительскому компоненту о размерах
              if (onDocumentDimensionsChange) {
                onDocumentDimensionsChange({
                  original: { width: originalWidth, height: originalHeight },
                  canvas: { width: newWidth, height: newHeight }
                });
              }
            };
            img.onerror = (err) => {
              console.error('Ошибка загрузки изображения PDF:', err);
            };
            img.src = dataURL;
          }
        }, 50);
      }
    } catch (error) {
      console.error('Ошибка конвертации PDF:', error);
    }
  };

  const handleImageLoad = (dimensions) => {
    // Устанавливаем оригинальные размеры изображения
    setOriginalDimensions(dimensions);
    
    // Вычисляем правильные размеры canvas для изображения
    const imageAspect = dimensions.width / dimensions.height;
    const maxWidth = (containerRef.current?.clientWidth || 800) - 40;
    const maxHeight = Math.max(600, (containerRef.current?.clientHeight || 1000) - 200);
    
    let newWidth = maxWidth;
    let newHeight = newWidth / imageAspect;
    
    if (newHeight > maxHeight) {
      newHeight = maxHeight;
      newWidth = newHeight * imageAspect;
    }
    
    const canvasSize = { width: newWidth, height: newHeight };
    setStageSize(canvasSize);
    
    // Сообщаем родительскому компоненту о размерах
    if (onDocumentDimensionsChange) {
      onDocumentDimensionsChange({
        original: dimensions,
        canvas: canvasSize
      });
    }
  };

  const pageStamps = placedStamps.filter(s => s.pageNumber === currentPage - 1);

  const handleStageClick = (e) => {
    try {
      // Если кликнули не на объект
      const stage = e.target.getStage();
      if (!stage) return;
      
      const clickedOnEmpty = e.target === stage;
      
      // Если кликнули на пустую область, снимаем выделение
      if (clickedOnEmpty) {
        setSelectedStampId(null);
        // Уведомляем родительский компонент об окончании редактирования
        if (onStampEditingChange) {
          onStampEditingChange(false);
        }
      }
    } catch (error) {
      console.error('Ошибка обработки клика на Stage:', error);
    }
  };

  const handleStampSelect = (stampId) => {
    setSelectedStampId(stampId);
    // Уведомляем родительский компонент о начале редактирования
    if (onStampEditingChange) {
      onStampEditingChange(true);
    }
  };

  const handleStampDragEnd = (e, stamp) => {
    const updatedStamp = {
      ...stamp,
      x: e.target.x(),
      y: e.target.y(),
    };
    onStampUpdate(updatedStamp);
  };

  const handleStampTransformEnd = (e, stamp) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    // Вычисляем абсолютные размеры после трансформации
    // node.width() возвращает текущую ширину элемента
    let newWidth = node.width() * scaleX;
    let newHeight = node.height() * scaleY;

    // Ограничиваем размеры: минимум 10px, максимум 200px
    const minSize = 10;
    const maxSize = 200;
    
    // Вычисляем соотношение сторон из оригинальных размеров
    const aspectRatio = stamp.width / stamp.height;
    
    // Применяем ограничения с сохранением пропорций
    if (newWidth < minSize) {
      newWidth = minSize;
      newHeight = minSize / aspectRatio;
    } else if (newWidth > maxSize) {
      newWidth = maxSize;
      newHeight = maxSize / aspectRatio;
    }
    
    if (newHeight < minSize) {
      newHeight = minSize;
      newWidth = minSize * aspectRatio;
    } else if (newHeight > maxSize) {
      newHeight = maxSize;
      newWidth = maxSize * aspectRatio;
    }

    // Сбрасываем scale обратно к 1
    node.scaleX(1);
    node.scaleY(1);

    const updatedStamp = {
      ...stamp,
      x: node.x(),
      y: node.y(),
      width: newWidth,
      height: newHeight,
      rotation: node.rotation(),
    };

    onStampUpdate(updatedStamp);
  };

  const handleRemoveStamp = () => {
    if (selectedStampId) {
      onRemoveStamp(selectedStampId);
      setSelectedStampId(null);
      // Уведомляем родительский компонент об окончании редактирования
      if (onStampEditingChange) {
        onStampEditingChange(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="document-viewer">
        <div className="viewer-loading">
          <Loader2 size={48} className="animate-spin" />
          <p>Загрузка документа...</p>
        </div>
      </div>
    );
  }

  if (!documentUrl) {
    return (
      <div className="document-viewer">
        <div className="viewer-error">
          <p>Документ не загружен</p>
        </div>
      </div>
    );
  }

  return (
    <div className="document-viewer">
      {/* Панель инструментов */}
      <div className="viewer-toolbar">
        {numPages && numPages > 1 && (
          <div className="page-controls">
            <button 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="toolbar-button"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="page-info">
              {currentPage} / {numPages}
            </span>
            <button 
              onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
              disabled={currentPage === numPages}
              className="toolbar-button"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>

      {/* Плавающие кнопки зума на правой стороне */}
      <div className="zoom-controls-floating">
        <button 
          onClick={() => {
            setScale(s => Math.min(2, s + 0.1));
            setStagePos({ x: 0, y: 0 }); // Сбрасываем позицию
          }}
          className="zoom-button"
          title="Увеличить"
        >
          <Plus size={18} />
        </button>
        <span className="zoom-info-floating">{Math.round(scale * 100)}%</span>
        <button 
          onClick={() => {
            setScale(s => Math.max(0.5, s - 0.1));
            setStagePos({ x: 0, y: 0 }); // Сбрасываем позицию
          }}
          className="zoom-button"
          title="Уменьшить"
        >
          <Minus size={18} />
        </button>
      </div>


      {/* Контейнер для канваса */}
      <div 
        className="document-container konva-container"
        ref={containerRef}
      >
        {documentType === 'pdf' ? (
          <>
            {/* Скрытый canvas для рендеринга PDF */}
            <div style={{ position: 'absolute', visibility: 'hidden', width: 'auto', height: 'auto' }}>
              <Document
                file={documentUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<Loader2 className="animate-spin" />}
              >
                <Page
                  pageNumber={currentPage}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  canvasRef={pdfCanvasRef}
                  onRenderSuccess={onPageRenderSuccess}
                />
              </Document>
            </div>

            {/* Konva Stage для интерактивных элементов */}
            {stageSize.width > 0 && stageSize.height > 0 && (
              <Stage
                width={stageSize.width}
                height={stageSize.height}
                scaleX={scale}
                scaleY={scale}
                x={stagePos.x}
                y={stagePos.y}
                onMouseDown={handleStageClick}
                onTouchStart={handleStageClick}
                ref={stageRef}
                style={{ border: '1px solid #ddd', background: 'white' }}
              >
              <Layer>
                {/* Фон документа (PDF страница как изображение) */}
                {pageImage && (
                  <KonvaImage
                    image={pageImage}
                    x={0}
                    y={0}
                    width={stageSize.width}
                    height={stageSize.height}
                    listening={false}
                  />
                )}

                {/* Размещенные печати */}
                {pageStamps.map((stamp) => (
                  <StampImage
                    key={stamp.id}
                    stamp={stamp}
                    isSelected={selectedStampId === stamp.id}
                    onSelect={() => handleStampSelect(stamp.id)}
                    onDragEnd={(e) => handleStampDragEnd(e, stamp)}
                    onTransformEnd={(e) => handleStampTransformEnd(e, stamp)}
                    imageUrl={stamp.imageUrl}
                  />
                ))}
              </Layer>
            </Stage>
            )}
          </>
        ) : (
          <>
            {stageSize.width > 0 && stageSize.height > 0 && (
              <Stage
                width={stageSize.width}
                height={stageSize.height}
                scaleX={scale}
                scaleY={scale}
                x={stagePos.x}
                y={stagePos.y}
                onMouseDown={handleStageClick}
                onTouchStart={handleStageClick}
                ref={stageRef}
                style={{ border: '1px solid #ddd', background: 'white' }}
              >
                <Layer>
                  {/* Изображение документа */}
                  <DocumentImage 
                    url={documentUrl} 
                    width={stageSize.width} 
                    height={stageSize.height}
                    onImageLoad={handleImageLoad}
                  />
                  
                  {/* Размещенные печати */}
                  {pageStamps.map((stamp) => (
                    <StampImage
                      key={stamp.id}
                      stamp={stamp}
                      isSelected={selectedStampId === stamp.id}
                      onSelect={() => handleStampSelect(stamp.id)}
                      onDragEnd={(e) => handleStampDragEnd(e, stamp)}
                      onTransformEnd={(e) => handleStampTransformEnd(e, stamp)}
                      imageUrl={stamp.imageUrl}
                    />
                  ))}
                </Layer>
              </Stage>
            )}
          </>
        )}
      </div>

      {/* Панель управления для выбранной печати */}
      {selectedStampId && (
        <div className="stamp-control-panel">
          <div className="control-panel-content">
            <div className="control-info">
              <p>Используйте маркеры для изменения размера и поворота</p>
            </div>
            <div className="action-buttons">
              <button 
                className="action-button remove-button"
                onClick={handleRemoveStamp}
              >
                <span>🗑️</span>
                <span>Удалить</span>
              </button>
              <button 
                className="action-button deselect-button"
                onClick={() => {
                  setSelectedStampId(null);
                  // Уведомляем родительский компонент об окончании редактирования
                  if (onStampEditingChange) {
                    onStampEditingChange(false);
                  }
                }}
              >
                <span>✓</span>
                <span>Готово</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Компонент для отображения изображения документа
function DocumentImage({ url, width, height, onImageLoad }) {
  const [image] = useImage(url || '');
  
  useEffect(() => {
    if (image && onImageLoad) {
      // Сообщаем родительскому компоненту об оригинальных размерах изображения
      onImageLoad({
        width: image.width,
        height: image.height
      });
    }
  }, [image, onImageLoad]);
  
  if (!image || !url) return null;
  
  const imageAspect = image.width / image.height;
  const stageAspect = width / height;
  
  let imgWidth = width;
  let imgHeight = height;
  
  if (imageAspect > stageAspect) {
    imgHeight = width / imageAspect;
  } else {
    imgWidth = height * imageAspect;
  }
  
  return (
    <KonvaImage
      image={image}
      width={imgWidth}
      height={imgHeight}
      x={(width - imgWidth) / 2}
      y={(height - imgHeight) / 2}
      listening={false}
    />
  );
}

export default DocumentViewer;
